import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { listWorkflows, STATUS_COLOR, WorkflowRow } from "../api/workflows";
import {
  isLiveEnv,
  listSearchEnvs,
  SearchEnv,
} from "../api/searchEnvs";
import { ApiError } from "../api/client";

// "My datasets" — export workflows the authenticated user submitted,
// presented with the lifecycle properties the user actually cares
// about when deciding what to do next: retention TTL, whether a
// search env is currently up, when it was created. Backend filters
// by submitted_by; admins see everyone's. We client-side-filter to
// export-kind workflows so indexing ops don't show up here (those
// live in /activity instead).
//
// The route stays /exports for URL stability; only the human-facing
// labels move to "Datasets". ``export`` is the verb that produced
// the dataset, not the noun describing the thing itself.
const EXPORT_KINDS = new Set(["batch_export", "month_export", "year_export"]);

export function ExportsList() {
  const [rows, setRows] = useState<WorkflowRow[] | null>(null);
  // Map of workflow_id → first non-terminal search env for that
  // workflow. Used to render the Search column without per-row
  // round-trips. Lives in its own state so we can refresh
  // independently of the workflow list (envs change faster).
  const [activeEnvByWorkflow, setActiveEnvByWorkflow] = useState<
    Record<string, SearchEnv>
  >({});
  const [error, setError] = useState<string | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    listWorkflows({ limit: 100 })
      .then((r) => setRows(r.workflows.filter((w) => EXPORT_KINDS.has(w.kind))))
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          setError(`API ${err.status}: ${JSON.stringify(err.body)}`);
        } else {
          setError(String(err));
        }
      });
  }, []);

  useEffect(() => {
    // Pull every non-terminal env in one shot — the typical install
    // has at most a handful in flight, so the 50-row default is
    // plenty. If we ever blow that cap we'll need a workflow_id
    // batched lookup.
    listSearchEnvs({ limit: 50 })
      .then((r) => {
        const m: Record<string, SearchEnv> = {};
        for (const env of r.search_envs) {
          if (isLiveEnv(env)) {
            // Only keep the first live env per workflow — if two
            // exist, the user's already paying for redundancy and
            // either link is useful here. ``failed`` envs count as
            // live because their domain is still up + still
            // accruing cost; the user needs to see + act on them.
            if (!m[env.workflow_id]) m[env.workflow_id] = env;
          }
        }
        setActiveEnvByWorkflow(m);
      })
      .catch(() => {
        // Soft-fail: an envs-fetch error shouldn't break the
        // listview. The Search column just stays empty.
      });
  }, []);

  // Tick once a minute so "expires in 2h" / "submitted 5m ago" stay
  // accurate without re-fetching the rows.
  useEffect(() => {
    const h = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(h);
  }, []);

  if (error)
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  if (!rows)
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <CircularProgress size={20} />
      </Container>
    );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">My datasets</Typography>
        <Button component={RouterLink} to="/exports/new" variant="contained">
          New dataset
        </Button>
      </Box>
      {rows.length === 0 ? (
        <Alert severity="info">
          No datasets yet.{" "}
          <Link component={RouterLink} to="/exports/new">
            Create your first one
          </Link>
          .
        </Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Months</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Size</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Search</TableCell>
              <TableCell>Submitted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((wf) => (
              <DatasetRow
                key={wf.id}
                wf={wf}
                activeEnv={activeEnvByWorkflow[wf.id]}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </Container>
  );
}

function DatasetRow({
  wf,
  activeEnv,
}: {
  wf: WorkflowRow;
  activeEnv: SearchEnv | undefined;
}) {
  const params = parseParams(wf.params_json);
  const name = datasetName(params, wf.id);
  const months = formatMonths(params);
  return (
    <TableRow hover>
      <TableCell>
        <Link component={RouterLink} to={`/exports/${wf.id}`}>
          {name}
        </Link>
      </TableCell>
      <TableCell>{months}</TableCell>
      <TableCell>
        <Chip
          size="small"
          label={wf.status}
          color={STATUS_COLOR[wf.status] ?? "default"}
        />
      </TableCell>
      <TableCell align="right">
        {wf.output_size_bytes ? (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {formatBytes(wf.output_size_bytes)}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <ExpiresChip wf={wf} />
      </TableCell>
      <TableCell>
        {activeEnv ? (
          <Link
            component={RouterLink}
            to={`/search-envs/${activeEnv.id}`}
            underline="none"
          >
            <Chip
              size="small"
              label={activeEnv.status}
              color="primary"
              variant="outlined"
              sx={{ cursor: "pointer" }}
            />
          </Link>
        ) : (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="caption" component="div">
          {wf.submitted_by}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {formatRelative(wf.submitted_at)}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

// TTL chip with semantics: unmanaged (no auto-delete), deleted (reaper
// has run), expired (past TTL but reaper hasn't yet), in-flight TTL.
// Color matches state severity so a scan of the column reads at a
// glance.
function ExpiresChip({ wf }: { wf: WorkflowRow }) {
  if (wf.output_deleted_at) {
    return (
      <Tooltip title={`deleted ${formatRelative(wf.output_deleted_at)}`}>
        <Chip
          size="small"
          label="deleted"
          color="default"
          variant="outlined"
        />
      </Tooltip>
    );
  }
  if (!wf.output_expires_at) {
    return (
      <Tooltip title="No managed retention — operator never registered this prefix (CLI-style hand-chosen path, or pre-v10 export)">
        <Chip
          size="small"
          label="unmanaged"
          variant="outlined"
          sx={{ opacity: 0.6 }}
        />
      </Tooltip>
    );
  }
  const ms = wf.output_expires_at - Date.now();
  if (ms <= 0) {
    return (
      <Tooltip
        title={`Past TTL since ${formatRelative(wf.output_expires_at)}; reaper will sweep on its next tick`}
      >
        <Chip size="small" label="expired" color="warning" />
      </Tooltip>
    );
  }
  const severity = ttlSeverity(ms);
  return (
    <Tooltip
      title={`Auto-deletes ${new Date(wf.output_expires_at).toLocaleString()}`}
    >
      <Chip
        size="small"
        label={`in ${formatTtl(ms)}`}
        color={severity}
        variant={severity === "default" ? "outlined" : "filled"}
      />
    </Tooltip>
  );
}

function ttlSeverity(ms: number): "default" | "warning" | "error" {
  if (ms < 60 * 60 * 1000) return "error"; // < 1h
  if (ms < 24 * 60 * 60 * 1000) return "warning"; // < 1d
  return "default";
}

function formatTtl(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

// "5m ago" / "3h ago" / "2d ago" / "May 21". Tuned so the listview's
// Submitted column doesn't waste a whole row on full ISO timestamps
// for rows the user typically scans by recency.
function formatRelative(ms: number): string {
  const diff = Date.now() - ms;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(ms).toLocaleDateString();
}

interface ParsedParams {
  output?: string;
  months?: string[];
  year?: string;
  month?: string;
}

function parseParams(raw: string): ParsedParams {
  try {
    return JSON.parse(raw) as ParsedParams;
  } catch {
    return {};
  }
}

// Pull the user-visible "name" off the output prefix. The portal-
// generated slug (YYYY-MM-DD-suffix or YYYY-MM-DD-descriptive-name)
// is what humans actually wrote; the workflow id hash isn't.
function datasetName(params: ParsedParams, workflowId: string): string {
  const out = params.output;
  if (typeof out === "string") {
    const segments = out
      .replace(/^s3:\/\/[^/]+\//, "")
      .split("/")
      .filter(Boolean);
    if (segments.length > 0) return segments[segments.length - 1];
  }
  return workflowId.slice(0, 12);
}

// Compact-byte formatter matching the bytes-style elsewhere in the
// SPA — KB / MB / GB / TB with one decimal place above MB. Keeps
// the Size column right-aligned and skinny in the listview.
function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n < 1024 ** 4) return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
  return `${(n / 1024 ** 4).toFixed(2)} TB`;
}

// Render the month-range covered by the dataset's source data.
// Single-month workflows show their month; multi-month batches show
// a range; out-of-band workflows fall back to the comma-joined list.
function formatMonths(params: ParsedParams): string {
  if (params.months && params.months.length > 0) {
    const sorted = [...params.months].sort();
    if (sorted.length === 1) return sorted[0];
    return `${sorted[0]} → ${sorted[sorted.length - 1]}`;
  }
  if (params.year && params.month) return `${params.year}-${params.month}`;
  if (params.year) return params.year;
  return "—";
}
